/**
 * «Свой VPN» — магазины, которые завели ваши клиенты.
 *
 * Показывает только собственное поддерево и только то, что оно принесло лично вам.
 * Клиент, открывший магазин, дальше сам себе владелец: его выручку, цены и клиентов
 * здесь не видно и видно быть не должно.
 */
import { createFileRoute, Link } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Store, ChevronLeft } from 'lucide-react';
import { useEffect } from 'react';
import { toast } from 'sonner';

import { api, type MirrorsOverview } from '@/lib/api';
import { getWebApp, haptic } from '@/lib/telegram';
import { Skeleton, SkeletonLine } from '@/components/ui/skeleton';

export const Route = createFileRoute('/mirrors')({ component: Mirrors });

const usd = (minor: number) => `$${(minor / 100).toFixed(2)}`;

function Mirrors() {
  const q = useQuery({ queryKey: ['mirrors'], queryFn: api.mirrors });

  useEffect(() => {
    const tg = getWebApp();
    const back = tg?.BackButton;
    if (!back) return;
    const go = () => window.history.back();
    const t = setTimeout(() => {
      back.show();
      back.onClick(go);
    }, 300);
    return () => {
      clearTimeout(t);
      back.offClick(go);
      back.hide();
    };
  }, []);

  if (q.isPending) return <MirrorsSkeleton />;

  const data = q.data;
  const rows = data?.rows ?? [];

  return (
    <div className="p-4 space-y-4">
      <h1 className="text-xl font-semibold">Свой VPN</h1>

      <div className="rounded-2xl bg-white/[0.04] p-4">
        <div className="text-xs text-white/50">Принесли вам за всё время</div>
        <div className="mt-1 text-3xl font-semibold tabular-nums">
          {usd(data?.total_earned_usd_minor ?? 0)}
        </div>
        <p className="mt-2 text-xs text-white/50">
          Клиент заводит собственный магазин, продаёт сам, а вы получаете долю с каждой
          его продажи. И с продаж тех, кого приведёт уже он.
        </p>
      </div>

      {/* Тумблер кнопки в каждом боте. Он и раньше был в карточке бота, но партнёры
          искали его здесь и писали «нельзя убрать плашку Свой VPN». */}
      {(data?.bots.length ?? 0) > 0 && (
        <div className="space-y-2">
          <div className="text-xs text-white/50 px-1">Кнопка «Свой VPN» в меню бота</div>
          <div className="rounded-2xl bg-white/[0.04] divide-y divide-white/[0.06]">
            {data!.bots.map((b) => (
              <BotToggleRow key={b.bot_id} bot={b} />
            ))}
          </div>
        </div>
      )}

      {!data?.enabled && (
        <div className="rounded-2xl border border-amber-500/30 bg-amber-500/10 p-4 text-sm">
          Кнопка выключена во всех ботах: новых магазинов не появится, открытые работают.
        </div>
      )}

      {rows.length === 0 ? (
        <div className="rounded-2xl bg-white/[0.04] p-8 text-center">
          <Store className="mx-auto h-8 w-8 text-white/25" strokeWidth={1.5} />
          <div className="mt-3 text-sm font-medium">Магазинов пока нет</div>
          <p className="mt-1 text-xs text-white/50">
            Как только кто-то из клиентов откроет свой, он появится здесь.
          </p>
          <Link
            to="/bots"
            className="mt-4 inline-flex items-center gap-1 text-xs text-white/70"
          >
            <ChevronLeft className="h-3.5 w-3.5" />К настройкам ботов
          </Link>
        </div>
      ) : (
        <div className="space-y-2">
          {rows.map((r) => (
            <div
              key={r.bot_id}
              className="flex items-center justify-between gap-3 rounded-2xl bg-white/[0.04] p-3"
            >
              <div className="min-w-0">
                <div className="truncate text-sm font-medium">@{r.username}</div>
                <div className="text-xs text-white/50">
                  {r.level === 1 ? 'Ваш прямой' : `Уровень ${r.level}`} ·{' '}
                  {r.clients_count} клиент(ов)
                </div>
              </div>
              <div className="shrink-0 tabular-nums text-sm font-medium">
                {usd(r.earned_usd_minor)}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function BotToggleRow({ bot }: { bot: MirrorsOverview['bots'][number] }) {
  const qc = useQueryClient();
  const { mutate, isPending } = useMutation({
    mutationFn: (next: boolean) => api.updateBot(bot.bot_id, { mirrors_enabled: next }),
    onMutate: (next: boolean) => {
      // Оптимистично: тумблер должен щёлкать сразу, а не после ответа.
      qc.setQueryData(['mirrors'], (prev: MirrorsOverview | undefined) => {
        if (!prev) return prev;
        const bots = prev.bots.map((b) => (b.bot_id === bot.bot_id ? { ...b, mirrors_enabled: next } : b));
        return { ...prev, bots, enabled: bots.some((b) => b.mirrors_enabled) };
      });
    },
    onSuccess: (_updated, next) => {
      qc.setQueryData(['bot', bot.bot_id], (prev: any) => prev && { ...prev, mirrors_enabled: next });
      haptic('success');
      toast.success(next ? 'Кнопка включена' : 'Кнопка выключена');
    },
    onError: () => {
      void qc.invalidateQueries({ queryKey: ['mirrors'] });
      haptic('error');
      toast.error('Не сохранилось');
    },
  });
  return (
    <label className="flex items-center justify-between gap-3 p-3.5 cursor-pointer">
      <span className="truncate text-sm font-medium">@{bot.username}</span>
      <input
        type="checkbox"
        checked={bot.mirrors_enabled}
        disabled={isPending}
        onChange={(e) => mutate(e.target.checked)}
        className="w-4 h-4 accent-foreground shrink-0"
      />
    </label>
  );
}

function MirrorsSkeleton() {
  return (
    <div className="p-4 space-y-4">
      <h1 className="text-xl font-semibold">Свой VPN</h1>
      <div className="rounded-2xl bg-white/[0.04] p-4">
        <SkeletonLine size="sm" className="w-40" />
        <Skeleton className="mt-2 h-9 w-32" />
        <SkeletonLine size="sm" className="mt-3 w-full" />
      </div>
      <div className="space-y-2">
        <SkeletonLine size="sm" className="w-48" />
        <div className="rounded-2xl bg-white/[0.04] divide-y divide-white/[0.06]">
          {[0, 1].map((i) => (
            <div key={i} className="flex items-center justify-between gap-3 p-3.5">
              <SkeletonLine size="sm" className="w-32" />
              <Skeleton className="h-4 w-4" />
            </div>
          ))}
        </div>
      </div>
      <div className="space-y-2">
        {[0, 1, 2].map((i) => (
          <div
            key={i}
            className="flex items-center justify-between gap-3 rounded-2xl bg-white/[0.04] p-3"
          >
            <div className="min-w-0 flex-1 space-y-1.5">
              <SkeletonLine size="sm" className="w-32" />
              <SkeletonLine size="sm" className="w-40" />
            </div>
            <Skeleton className="h-5 w-14" />
          </div>
        ))}
      </div>
    </div>
  );
}
